import React, { useEffect, useRef, useCallback } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './InkDrop.css';

const PROMPT = '마우스 커서가 잉크 방울을 떨어뜨리는 캔버스 — 마우스를 움직이면 커서 위치에서 잉크 방울이 떨어지고, 방울끼리 만나면 수묵화처럼 번지면서 섞여. 마우스 속도가 빠르면 잉크가 튀는 스플래시 효과, 느리면 천천히 퍼지는 번짐 효과.';

const MAX_DROPS = 400;
const DRY_START = 240;   // frames before drying begins
const DRY_DURATION = 360; // frames to fully dry
const SPREAD_SPEED = 0.08;
const BLEED_DISTANCE = 40;
const MAX_RADIUS = 30;    // cap on how big a drop can grow

interface InkDrop {
  x: number;
  y: number;
  radius: number;
  targetRadius: number;
  opacity: number;
  maxOpacity: number;
  age: number;
  ink: number; // ink density 0-1
  vx: number;
  vy: number;
  isSplash: boolean;
}

const InkDropExperiment: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dropsRef = useRef<InkDrop[]>([]);
  const animRef = useRef<number>(0);
  const mouseRef = useRef({ x: -100, y: -100, prevX: -100, prevY: -100, active: false });
  const frameRef = useRef(0);
  const dropCountRef = useRef(0);
  const exp = experiments.find(e => e.id === 'ink-drop');

  const spawnDrop = useCallback((x: number, y: number, speed: number) => {
    const drops = dropsRef.current;

    if (speed > 8) {
      // Fast: splash — multiple small drops scattered
      const count = Math.min(Math.floor(speed / 5), 5);
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * speed * 0.8;
        const splashSpeed = 0.5 + Math.random() * speed * 0.15;
        const size = 1.5 + Math.random() * 2.5;

        if (drops.length >= MAX_DROPS) drops.shift();
        drops.push({
          x: x + Math.cos(angle) * dist * 0.3,
          y: y + Math.sin(angle) * dist * 0.3,
          radius: size * 0.3,
          targetRadius: Math.min(size, MAX_RADIUS * 0.3),
          opacity: 0.6 + Math.random() * 0.3,
          maxOpacity: 0.6 + Math.random() * 0.3,
          age: 0,
          ink: 0.5 + Math.random() * 0.3,
          vx: Math.cos(angle) * splashSpeed,
          vy: Math.sin(angle) * splashSpeed,
          isSplash: true,
        });
      }
    }

    // Main drop — always spawn
    const baseSize = Math.max(2, Math.min(10, 4 + (15 - speed) * 0.3));
    const spreadTarget = Math.min(MAX_RADIUS, baseSize + (speed < 4 ? 6 + Math.random() * 4 : 2 + Math.random() * 3));

    if (drops.length >= MAX_DROPS) drops.shift();
    drops.push({
      x,
      y,
      radius: baseSize * 0.3,
      targetRadius: spreadTarget,
      opacity: 0.7 + Math.random() * 0.2,
      maxOpacity: 0.7 + Math.random() * 0.2,
      age: 0,
      ink: 0.6 + Math.random() * 0.3,
      vx: 0,
      vy: 0,
      isSplash: false,
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current.prevX = mouseRef.current.x;
      mouseRef.current.prevY = mouseRef.current.y;
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      mouseRef.current.prevX = mouseRef.current.x;
      mouseRef.current.prevY = mouseRef.current.y;
      mouseRef.current.x = t.clientX;
      mouseRef.current.y = t.clientY;
      mouseRef.current.active = true;
    };

    const onTouchEnd = () => {
      mouseRef.current.active = false;
    };

    const onMouseLeave = () => {
      mouseRef.current.active = false;
    };

    let spawnAccum = 0;

    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      frameRef.current++;

      const w = window.innerWidth;
      const h = window.innerHeight;
      const drops = dropsRef.current;

      // Spawn drops from mouse movement
      if (mouseRef.current.active) {
        const dx = mouseRef.current.x - mouseRef.current.prevX;
        const dy = mouseRef.current.y - mouseRef.current.prevY;
        const speed = Math.sqrt(dx * dx + dy * dy);

        spawnAccum += speed;
        const spawnThreshold = speed > 15 ? 4 : speed > 6 ? 8 : 14;

        if (spawnAccum >= spawnThreshold) {
          spawnDrop(mouseRef.current.x, mouseRef.current.y, speed);
          spawnAccum = 0;
        }

        mouseRef.current.prevX = mouseRef.current.x;
        mouseRef.current.prevY = mouseRef.current.y;
      }

      // Clear with paper background
      ctx.fillStyle = '#f5f0e6';
      ctx.fillRect(0, 0, w, h);

      // Paper texture — subtle noise
      if (frameRef.current === 1) {
        // Draw texture once (we redraw background each frame anyway for simplicity)
      }

      // Update & render drops
      for (let i = drops.length - 1; i >= 0; i--) {
        const d = drops[i];
        d.age++;

        // Spread
        if (d.radius < d.targetRadius) {
          d.radius += (d.targetRadius - d.radius) * SPREAD_SPEED;
        }

        // Splash physics
        if (d.isSplash && d.age < 30) {
          d.x += d.vx;
          d.y += d.vy;
          d.vx *= 0.92;
          d.vy *= 0.92;
          d.vy += 0.08; // gravity
        }

        // Bleed into nearby drops — increase target radius if close
        for (let j = i + 1; j < drops.length; j++) {
          const other = drops[j];
          const ddx = d.x - other.x;
          const ddy = d.y - other.y;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          const touchDist = d.radius + other.radius;

          if (dist < BLEED_DISTANCE && dist < touchDist * 1.5) {
            // Bleed effect — both drops expand slightly toward each other
            const bleedFactor = 0.005 * (1 - dist / BLEED_DISTANCE);
            if (d.targetRadius < MAX_RADIUS) d.targetRadius += bleedFactor;
            if (other.targetRadius < MAX_RADIUS) other.targetRadius += bleedFactor;
            // Ink transfer — dilute when merging
            const avgInk = (d.ink + other.ink) / 2;
            d.ink += (avgInk - d.ink) * 0.005;
            other.ink += (avgInk - other.ink) * 0.005;
          }
        }

        // Drying
        if (d.age > DRY_START) {
          const dryProgress = Math.min((d.age - DRY_START) / DRY_DURATION, 1);
          d.opacity = d.maxOpacity * (1 - dryProgress * 0.85);
        }

        // Remove fully dried drops
        if (d.age > DRY_START + DRY_DURATION + 60) {
          drops.splice(i, 1);
          continue;
        }

        // Render drop
        if (d.opacity <= 0.01) continue;

        const gradient = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, d.radius);
        const inkVal = Math.round(30 * (1 - d.ink));

        // Core — darkest
        gradient.addColorStop(0, `rgba(${inkVal}, ${inkVal}, ${inkVal + 5}, ${d.opacity * 0.9})`);
        // Mid — ink wash
        gradient.addColorStop(0.4, `rgba(${inkVal + 15}, ${inkVal + 15}, ${inkVal + 20}, ${d.opacity * 0.6})`);
        // Edge — feathered
        gradient.addColorStop(0.75, `rgba(${inkVal + 30}, ${inkVal + 25}, ${inkVal + 25}, ${d.opacity * 0.25})`);
        // Outer — transparent
        gradient.addColorStop(1, `rgba(${inkVal + 40}, ${inkVal + 35}, ${inkVal + 35}, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw connecting ink bridges between close drops
      ctx.lineWidth = 1;
      for (let i = 0; i < drops.length; i++) {
        const a = drops[i];
        if (a.opacity < 0.15) continue;
        for (let j = i + 1; j < drops.length; j++) {
          const b = drops[j];
          if (b.opacity < 0.15) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const touchDist = a.radius + b.radius;

          if (dist < touchDist * 0.9) {
            const alpha = Math.min(a.opacity, b.opacity) * 0.4 * (1 - dist / touchDist);
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;
            const bulge = Math.min(a.radius, b.radius) * 0.5;

            ctx.fillStyle = `rgba(20, 20, 25, ${alpha})`;
            ctx.beginPath();
            ctx.ellipse(midX, midY, dist / 2, bulge, Math.atan2(dy, dx), 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      dropCountRef.current = drops.length;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('resize', resize);

    resize();
    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', resize);
    };
  }, [spawnDrop]);

  return (
    <div className="inkDrop">
      <canvas ref={canvasRef} className="inkCanvas" />

      <ExperimentNav prompt={exp?.prompt || PROMPT} title="Ink Drop" />

      <div className="inkHint">draw with your cursor</div>

      <div className="inkHud">
        <span className="hudLabel">ink drop</span>
        <span className="hudDivider">—</span>
        <span className="hudLabel">move to draw, speed changes stroke</span>
      </div>
    </div>
  );
};

export default InkDropExperiment;
